import EditIcon from '@mui/icons-material/Edit'
import { Button, Chip, Stack, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import * as endPoints from '@/endpoints'
import { fuelFreshness } from '@/features/dashboard/power/group/groupDisplay'
import { flavourLabel } from '@/features/dashboard/site-admin/orgFlavours'
import useAuth from '@/hooks/useAuth'
import axios from '@/lib/axios'
import { SITE_ADMIN_ROLES } from '@/lib/roles'
import { hasRole } from '@/utils/hasRole'
import PeekLayout, {
    PeekDetails,
    PeekPlaceholder,
    PeekSection
} from './PeekLayout'

const FUELS = [
    { key: 'electricity', label: 'Electricity', flag: 'has_electricity' },
    { key: 'gas', label: 'Gas', flag: 'has_gas' }
]

const METERS = [
    { key: 'electricity', label: 'electricity' },
    { key: 'gas', label: 'gas' },
    { key: 'water', label: 'water' }
]

const fuels = (org) =>
    FUELS.filter(({ flag }) => org[flag])
        .map(({ label }) => label)
        .join(', ') || 'None yet'

const meters = (counts = {}) =>
    METERS.filter(({ key }) => counts[key] > 0)
        .map(({ key, label }) => `${counts[key]} ${label}`)
        .join(' · ') || 'None yet'

const formatDate = (iso) =>
    DateTime.fromISO(iso).toLocaleString(DateTime.DATE_MED)

function Freshness({ fuel }) {
    const { label, color } = fuelFreshness(fuel)
    return (
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
            <Chip size="small" label={label} color={color} variant="outlined" />
            {fuel?.last_reading_at ? (
                <Typography variant="body2" color="text.secondary">
                    Last reading {formatDate(fuel.last_reading_at)}
                </Typography>
            ) : null}
        </Stack>
    )
}

Freshness.propTypes = { fuel: PropTypes.object }

/**
 * A member organisation, for the people who oversee it from outside: group
 * admins (their group's members), partner admins (their partner's orgs) and
 * site admins. Reads the org summary endpoint, which each of those can reach —
 * unlike the site-admin org record OrganisationPeek uses. Site admins also get
 * a way into the full Edit Organisation page.
 */
export default function OrgSummaryPeek({ id }) {
    const { user } = useAuth()
    const query = useQuery({
        queryKey: ['org-summary', id],
        queryFn: () =>
            axios
                .get(endPoints.ORG_SUMMARY(id))
                .then((response) => response.data),
        retry: false
    })
    const org = query.data

    if (!org) {
        return <PeekPlaceholder kind="Organisation" loading={query.isLoading} />
    }

    // A fuel the org doesn't take and has no readings for would only ever say
    // "No data" — the same rule the group summary list uses for gas.
    const freshness = FUELS.filter(
        ({ key, flag }) => org[flag] || !org.freshness?.[key]?.no_data
    )

    return (
        <PeekLayout
            kind="Organisation"
            title={org.name}
            subtitle={[org.short_name, flavourLabel(org.flavour)]
                .filter(Boolean)
                .join(' · ')}
            actions={
                hasRole(user, SITE_ADMIN_ROLES) ? (
                    <Button
                        component={RouterLink}
                        to={`/site-admin/orgs/${org.id}/edit`}
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon fontSize="small" />}
                    >
                        Edit organisation
                    </Button>
                ) : null
            }
        >
            <PeekSection title="Details">
                <PeekDetails
                    rows={[
                        { label: 'Users', value: org.user_count ?? '—' },
                        { label: 'Fuels', value: fuels(org) },
                        {
                            label: 'Locations',
                            value: org.location_count ?? '—'
                        },
                        { label: 'Meters', value: meters(org.meter_counts) },
                        {
                            label: 'Set up',
                            value: org.setup_completed_at
                                ? formatDate(org.setup_completed_at)
                                : 'Not finished'
                        }
                    ]}
                />
            </PeekSection>

            <PeekSection title="Data freshness">
                {freshness.length > 0 ? (
                    <PeekDetails
                        rows={freshness.map(({ key, label }) => ({
                            label,
                            value: <Freshness fuel={org.freshness?.[key]} />
                        }))}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No meter readings yet.
                    </Typography>
                )}
            </PeekSection>
        </PeekLayout>
    )
}

OrgSummaryPeek.propTypes = {
    id: PropTypes.string.isRequired
}
